import { useMemo } from "react";
import { LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer } from "recharts";
import { fmtNum } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { Award, RefreshCw, Lightbulb, Loader2, TrendingUp, TrendingDown, History } from "lucide-react";

const COMPONENT_META = [
  { key: "volume", label: "Volume", hint: "token count vs 1M target" },
  { key: "diversity", label: "Diversity", hint: "spread across domains" },
  { key: "uniqueness", label: "Uniqueness", hint: "free of near-duplicates" },
  { key: "relevance", label: "Relevance", hint: "matches your keywords" },
  { key: "richness", label: "Richness", hint: "substantial page bodies" },
];

const gradeTone = (score) =>
  score >= 80 ? { text: "text-emerald-300", ring: "#34d399", bg: "bg-emerald-500/10", border: "border-emerald-500/40" }
  : score >= 55 ? { text: "text-cyan-300", ring: "#22d3ee", bg: "bg-cyan-500/10", border: "border-cyan-500/40" }
  : score >= 40 ? { text: "text-amber-300", ring: "#fbbf24", bg: "bg-amber-500/10", border: "border-amber-500/40" }
  : { text: "text-rose-300", ring: "#f43f5e", bg: "bg-rose-500/10", border: "border-rose-500/40" };

const Bar = ({ label, hint, value, weight, color }) => (
  <div data-testid={`quality-bar-${label.toLowerCase()}`}>
    <div className="flex items-baseline justify-between gap-2">
      <span className="text-xs text-slate-300">{label}</span>
      <span className="font-mono text-[11px] text-slate-500 tabular-nums">{Math.round(value * 100)}<span className="text-slate-700"> · w{weight}</span></span>
    </div>
    <div className="mt-1 h-1.5 rounded-full bg-slate-800 overflow-hidden">
      <div className="h-full rounded-full transition-[width] duration-700 ease-out"
        style={{ width: `${Math.max(2, value * 100)}%`, background: color }} />
    </div>
    <p className="text-[10px] font-mono text-slate-600 mt-0.5">{hint}</p>
  </div>
);

export const QualityScore = ({ report, history, loading, onRefresh }) => {
  const score = report?.score ?? 0;
  const tone = useMemo(() => gradeTone(score), [score]);
  const dash = 2 * Math.PI * 52;
  const runs = history?.history || [];
  const delta = history?.delta;

  if (!report?.available) {
    return (
      <div data-testid="quality-panel-empty" className="rounded-lg border border-border bg-[#0B0F18] p-5">
        <div className="flex items-center gap-2 mb-2">
          <Award className="h-4 w-4 text-slate-500" />
          <h3 className="font-display font-bold text-base text-slate-300">Corpus Quality Score</h3>
        </div>
        <p className="text-xs text-slate-500 font-mono">
          {loading ? "Scoring corpus…" : "Collect some pages first — the score grades volume, diversity, uniqueness, relevance and richness."}
        </p>
      </div>
    );
  }

  const m = report.metrics;

  return (
    <div data-testid="quality-panel" className="rounded-lg border border-border bg-[#0B0F18] overflow-hidden">
      <div className="flex items-center gap-2 px-5 py-3 border-b border-border">
        <Award className="h-4 w-4 text-cyan-400" />
        <h3 className="font-display font-bold text-base">Corpus Quality Score</h3>
        <Button size="sm" variant="ghost" onClick={onRefresh} disabled={loading}
          data-testid="quality-recompute-button" className="ml-auto h-7 text-slate-400 hover:text-cyan-300">
          {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
          <span className="ml-1.5 font-mono text-[11px] uppercase">Recompute</span>
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 p-5">
        {/* Gauge */}
        <div className="lg:col-span-3 flex items-center gap-5 lg:flex-col lg:gap-3">
          <div className="relative h-[132px] w-[132px] shrink-0">
            <svg viewBox="0 0 120 120" className="h-full w-full -rotate-90">
              <circle cx="60" cy="60" r="52" fill="none" stroke="#1e293b" strokeWidth="9" />
              <circle cx="60" cy="60" r="52" fill="none" stroke={tone.ring} strokeWidth="9" strokeLinecap="round"
                strokeDasharray={dash} strokeDashoffset={dash * (1 - score / 100)}
                style={{ transition: "stroke-dashoffset 900ms cubic-bezier(.16,1,.3,1)" }} />
            </svg>
            <div className="absolute inset-0 grid place-items-center">
              <div className="text-center">
                <p data-testid="quality-score-value" className={cn("font-mono font-black text-4xl leading-none tabular-nums", tone.text)}>{score}</p>
                <p className="text-[10px] font-mono uppercase tracking-widest text-slate-600 mt-1">/ 100</p>
              </div>
            </div>
          </div>
          <div className="lg:text-center">
            <span data-testid="quality-grade-badge"
              className={cn("inline-block px-3 py-1 rounded-md border font-mono font-bold text-sm", tone.bg, tone.border, tone.text)}>
              GRADE {report.grade}
            </span>
            {typeof delta === "number" && delta !== 0 && (
              <span data-testid="quality-delta-badge"
                className={cn("ml-2 inline-flex items-center gap-1 px-2 py-1 rounded-md border font-mono text-[11px]",
                  delta > 0 ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-300"
                            : "border-rose-500/40 bg-rose-500/10 text-rose-300")}>
                {delta > 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                {delta > 0 ? "+" : ""}{delta} since last run
              </span>
            )}
            <p className="text-[11px] font-mono text-slate-600 mt-2">{fmtNum(m.tokens)} tokens · {fmtNum(m.pages)} pages</p>
          </div>
        </div>

        {/* Component bars */}
        <div className="lg:col-span-5 space-y-3.5">
          {COMPONENT_META.map((c) => (
            <Bar key={c.key} label={c.label} hint={c.hint}
              value={report.components[c.key]} weight={report.weights[c.key]} color={tone.ring} />
          ))}
        </div>

        {/* Facts + tips */}
        <div className="lg:col-span-4 space-y-4">
          <div className="grid grid-cols-2 gap-2" data-testid="quality-metrics">
            {[
              ["Avg tokens/page", fmtNum(m.avg_tokens)],
              ["Domains", fmtNum(m.domains)],
              ["Duplicates", `${fmtNum(m.duplicates)} (${Math.round(m.duplicate_ratio * 100)}%)`],
              ["Thin pages", fmtNum(m.thin_pages)],
            ].map(([k, v]) => (
              <div key={k} className="rounded-md border border-border bg-[#0E1420] px-2.5 py-2">
                <p className="text-[9px] font-mono uppercase tracking-wider text-slate-600">{k}</p>
                <p className="font-mono text-sm text-slate-200 tabular-nums mt-0.5">{v}</p>
              </div>
            ))}
          </div>

          <div className="rounded-md border border-border bg-[#0E1420]/60 p-3">
            <div className="flex items-center gap-1.5 mb-2">
              <Lightbulb className="h-3.5 w-3.5 text-amber-400" />
              <span className="font-mono text-[10px] uppercase tracking-widest text-slate-400">How to improve</span>
            </div>
            <ul className="space-y-1.5" data-testid="quality-tips">
              {report.tips?.map((t, i) => (
                <li key={i} className="text-[11px] text-slate-400 leading-relaxed flex gap-1.5">
                  <span className="text-cyan-500">›</span>{t}
                </li>
              ))}
            </ul>
          </div>

          {report.top_domains?.length > 1 && (
            <div className="space-y-1" data-testid="quality-top-domains">
              <p className="font-mono text-[10px] uppercase tracking-widest text-slate-600">Top domains</p>
              {report.top_domains.slice(0, 5).map((d) => (
                <div key={d.domain} className="flex items-center gap-2">
                  <span className="font-mono text-[11px] text-slate-400 truncate flex-1">{d.domain}</span>
                  <div className="h-1 w-20 rounded-full bg-slate-800 overflow-hidden">
                    <div className="h-full bg-cyan-500/70" style={{ width: `${(d.pages / m.pages) * 100}%` }} />
                  </div>
                  <span className="font-mono text-[10px] text-slate-600 w-8 text-right tabular-nums">{d.pages}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {runs.length > 1 && (
        <div className="border-t border-border px-5 py-4" data-testid="quality-history">
          <div className="flex items-center gap-2 mb-2">
            <History className="h-3.5 w-3.5 text-purple-400" />
            <span className="font-mono text-[10px] uppercase tracking-widest text-slate-400">Score history</span>
            <span className="ml-auto font-mono text-[11px] text-slate-600">{runs.length} snapshots</span>
          </div>
          <div className="h-[140px]" data-testid="quality-history-chart">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={runs.map((r, i) => ({ run: i + 1, score: r.score, pages: r.pages }))}
                margin={{ top: 6, right: 12, left: -20, bottom: 0 }}>
                <CartesianGrid stroke="#1e293b" strokeDasharray="2 4" vertical={false} />
                <XAxis dataKey="run" tick={{ fill: "#475569", fontSize: 10, fontFamily: "monospace" }}
                  tickLine={false} axisLine={{ stroke: "#1e293b" }} />
                <YAxis domain={[0, 100]} tick={{ fill: "#475569", fontSize: 10, fontFamily: "monospace" }}
                  tickLine={false} axisLine={false} />
                <Tooltip contentStyle={{ background: "#04060A", border: "1px solid #334155", borderRadius: 6,
                  fontFamily: "monospace", fontSize: 11 }} labelFormatter={(v) => `run ${v}`} />
                <Line type="monotone" dataKey="score" stroke={tone.ring} strokeWidth={2} isAnimationActive={false}
                  dot={{ r: 3, fill: tone.ring }} name="score" />
                <Line type="monotone" dataKey="pages" stroke="#64748b" strokeWidth={1} strokeDasharray="3 3"
                  isAnimationActive={false} dot={false} name="pages" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </div>
  );
};

export default QualityScore;
